
import {
  defineComponent,
  reactive,
  computed,
  watch,
  ref,
} from 'vue';

import { DatePicker } from 'v-calendar';
import 'v-calendar/dist/style.css';

declare interface IoDate {
  format: (format: string) => string;
}

export default defineComponent({
  emits: ['input'],

  components: {
    DatePicker,
  },

  props: {
    value: {
      type: [String, Number, Date],
      default: () => null,
    },
    minDate: {
      type: [String, Number, Date],
      default: () => null,
    },
    maxDate: {
      type: [String, Number, Date],
      default: () => null,
    },
    disableWeekend: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },

  setup(props, context) {
    const datepicker = ref();
    let defaultValue = null as Date | null;
    if (props.value !== null && props.value !== '') {
      defaultValue = new Date(props.value);
    }

    const field = reactive({
      date: defaultValue,
      dateText: '',
      attributes: [{
        timezone: 'Europe/Amsterdam',
      }],
      locale: 'de',
    });

    const formattedDateValue = computed(() => {
      const date = field.date as unknown as IoDate;

      return field.date !== null ? date.format('Y-m-d') : null;
    });

    const emitInput = () => context.emit('input', formattedDateValue.value);

    watch(() => field.date, () => {
      emitInput();
    });

    const onInputKeyDown = (event: KeyboardEvent, callback: unknown) => {
      if (['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '.'].indexOf(event.key) >= 0) {
        if (typeof callback === 'function') {
          callback();
        }
      }
    };

    const getDatepickerInputDate = (value: string) => {
      const datePartials = value.split('.');
      const validDateValue = datePartials.length === 3
        && (datePartials[0].length === 1 || datePartials[0].length === 2)
        && (datePartials[1].length === 1 || datePartials[1].length === 2)
        && datePartials[2].length === 4;

      if (validDateValue) {
        return new Date(
          parseInt(datePartials[2], 10),
          parseInt(datePartials[1], 10) - 1,
          parseInt(datePartials[0], 10),
        );
      }

      return null;
    };

    const updateValueByInput = (e: {
      target: {
        value: string,
      }
    }) => {
      const inputDate = getDatepickerInputDate(e.target.value);

      if (datepicker.value && inputDate !== null) {
        datepicker.value.updateValue(inputDate);
        datepicker.value.move(inputDate);
        datepicker.value.hidePopover();
      }
    };

    const disabledDates = computed(() => {
      if (props.disableWeekend) {
        return {
          weekdays: [
            1,
            7,
          ],
        };
      }

      return null;
    });

    return {
      field,
      formattedDateValue,
      datepicker,
      disabledDates,
      emitInput,
      getDatepickerInputDate,
      updateValueByInput,
      onInputKeyDown,
    };
  },
});
